// Cross-cluster bridges from /translate/$language/$doctype/in/$location pages
// to marriage, apostille, police-clearance, and notary clusters.
//
// Why: the triple-key matrix sits between language + doctype + location, but
// users almost always need a downstream service (jdทะเบียนสมรส, Apostille,
// PCSC, Notary). Surfacing those routes closes the journey and links the
// translation cluster into 4 high-authority hubs.

import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

// language slug → marriage nationality slug
const LANG_TO_NATIONALITY: Record<string, string> = {
  english: "american",
  japanese: "japanese",
  chinese: "chinese",
  korean: "korean",
  german: "german",
  french: "french",
  spanish: "spanish",
  italian: "italian",
  russian: "russian",
  portuguese: "portuguese",
  dutch: "dutch",
  swedish: "swedish",
  norwegian: "norwegian",
};

// language slug → apostille country slug
const LANG_TO_APOSTILLE: Record<string, string> = {
  english: "usa",
  japanese: "japan",
  chinese: "china",
  korean: "south-korea",
  german: "germany",
  french: "france",
  spanish: "spain",
  italian: "italy",
  russian: "russia",
  portuguese: "portugal",
  dutch: "netherlands",
  swedish: "sweden",
  norwegian: "norway",
  arabic: "uae",
  vietnamese: "vietnam",
  burmese: "myanmar",
  khmer: "cambodia",
  "malay-indonesian": "malaysia",
};

// language slug → police target country slug
const LANG_TO_POLICE: Record<string, string> = {
  english: "usa",
  japanese: "japan",
  chinese: "china",
  korean: "south-korea",
  german: "germany",
  french: "france",
  spanish: "spain",
  italian: "italy",
  russian: "russia",
  portuguese: "portugal",
  dutch: "netherlands",
  swedish: "sweden",
  norwegian: "norway",
  vietnamese: "vietnam",
};

// doctype → recommended notary act slug
const DOCTYPE_TO_NOTARY: Record<string, string> = {
  "power-of-attorney": "certify-signature",
  affidavit: "affidavit",
  "id-card": "copy-id",
  passport: "copy-passport",
  transcript: "copy-education",
  diploma: "copy-education",
  "company-affidavit": "certified-true-copy",
  contract: "certify-signature",
};

interface Props {
  mode: "th" | "en";
  language: string;          // language slug
  doctype: string;           // doctype slug
  languageNameTh: string;
  languageNameEn: string;
  doctypeNameTh: string;
  doctypeNameEn: string;
  countryNameTh?: string;    // resolved country name for nicer copy
  countryNameEn?: string;
}

interface Card {
  href: string;
  tagTh: string;
  tagEn: string;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
}

export default function TripleKeyCrossLinks(props: Props) {
  const {
    mode, language, doctype,
    languageNameTh, languageNameEn,
    doctypeNameTh, doctypeNameEn,
  } = props;
  const isTh = mode === "th";

  const cards: Card[] = [];

  // 1) Marriage hub (when doctype = marriage/birth/divorce + we have nationality)
  const nationality = LANG_TO_NATIONALITY[language];
  if (nationality && ["marriage-certificate", "birth-certificate", "divorce-certificate"].includes(doctype)) {
    cards.push({
      href: isTh
        ? `/marriage/${nationality}`
        : `/en/marriage`,
      tagTh: "จดทะเบียนสมรส",
      tagEn: "Marriage Registration",
      titleTh: `จดทะเบียนสมรสกับชาว${languageNameTh}`,
      titleEn: `Marry a ${languageNameEn} national in Thailand`,
      descTh: `ขั้นตอนครบ — แปล${doctypeNameTh}, รับรอง MFA, สถานทูต, จดทะเบียนที่อำเภอ`,
      descEn: `End-to-end: ${doctypeNameEn} translation, MFA + embassy legalisation, district office filing`,
    });
  }

  // 2) Apostille / embassy legalisation
  const apostille = LANG_TO_APOSTILLE[language];
  if (apostille && !["id-card"].includes(doctype)) {
    cards.push({
      href: isTh
        ? `/apostille/${apostille}/${doctype}`
        : `/en/apostille/${apostille}/${doctype}`,
      tagTh: "Apostille / รับรองสถานทูต",
      tagEn: "Apostille / Legalisation",
      titleTh: `${doctypeNameTh} → ใช้ที่${languageNameTh.replace(/^ภาษา/, "")}`,
      titleEn: `${doctypeNameEn} for use in ${languageNameEn.replace(/^/, "")}`,
      descTh: `MFA + รับรองสถานทูตปลายทาง — แพ็คเกจครบจบในที่เดียว`,
      descEn: `MFA legalisation + destination embassy/consulate stamps — one package`,
    });
  }

  // 3) Police clearance for target country
  const police = LANG_TO_POLICE[language];
  if (police && doctype === "police-clearance") {
    cards.push({
      href: isTh
        ? `/police-clearance/for/${police}`
        : `/en/police-clearance/for/${police}`,
      tagTh: "Police Clearance",
      tagEn: "Police Clearance",
      titleTh: `ใบรับรองความประพฤติเพื่อยื่น${languageNameTh.replace(/^ภาษา/, "")}`,
      titleEn: `Thai PCSC for ${languageNameEn.replace(/^/, "")} visa`,
      descTh: `รับใบ PCSC ภายใน 7-14 วัน พร้อมแปล${languageNameTh}และรับรอง MFA`,
      descEn: `PCSC issued in 7-14 days, includes ${languageNameEn} translation + MFA`,
    });
  }

  // 4) Notary act mapped from doctype
  const notaryAct = DOCTYPE_TO_NOTARY[doctype];
  if (notaryAct) {
    cards.push({
      href: isTh ? `/notary/${notaryAct}` : `/en/notary/${notaryAct}`,
      tagTh: "Notary Public",
      tagEn: "Notary Public",
      titleTh: `Notary สำหรับ${doctypeNameTh}`,
      titleEn: `Notary Public for ${doctypeNameEn}`,
      descTh: `Notarial Services Attorney ขึ้นทะเบียนสภาทนายความ — ใช้ได้ทั่วโลก`,
      descEn: `Notarial Services Attorney registered with Lawyers Council — globally accepted`,
    });
  }

  if (cards.length === 0) return null;

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh ? "บริการต่อเนื่องที่คุณอาจต้องการ" : "Services you may need next"}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        {isTh
          ? `งานแปล${doctypeNameTh}เป็น${languageNameTh}มักเป็นเพียงขั้นตอนหนึ่ง — ดูบริการที่เชื่อมต่อกันด้านล่าง`
          : `Translating ${doctypeNameEn} into ${languageNameEn} is usually one step in a longer process — explore the connected services below.`}
      </p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <Link
            key={c.href}
            to={c.href}
            className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
          >
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
              {isTh ? c.tagTh : c.tagEn}
            </div>
            <div className="mt-1 flex items-start justify-between gap-2">
              <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                {isTh ? c.titleTh : c.titleEn}
              </div>
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              {isTh ? c.descTh : c.descEn}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
